import { Flex, Modal, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type {
  LeadImportPreviewMatch,
  LeadImportPreviewRow
} from "../api/services.types.ts"
import {
  REASON_COLOUR,
  REASON_LABEL,
  matchAgeLabel,
  matchStateLabel
} from "../../Lead/LeadAddPage/wizard/lead-match-vocabulary.ts"

interface LeadImportPreviewModalProps {
  open: boolean
  rows: LeadImportPreviewRow[]
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * The report of what an upload would collide with. Advisory only: Import
 * Anyway is always available, because a returning customer is a real second
 * job and the file is the user to fix, not the system to refuse.
 */
export function LeadImportPreviewModal({
  open,
  rows,
  loading,
  onConfirm,
  onCancel
}: LeadImportPreviewModalProps) {
  const { t } = useTranslation()

  const colliding = rows.filter((row) => row.matches.length > 0)

  const describe = (match: LeadImportPreviewMatch) => {
    if (match.in_file_row != null) {
      return t("leadImportPreview.inFile", { row: match.in_file_row })
    }
    return [
      match.lead_name ?? t("leadMatch.hiddenLead"),
      matchStateLabel(match.match_state, t),
      match.owner_name && t("leadMatch.owner", { name: match.owner_name }),
      matchAgeLabel(match.created, t)
    ]
      .filter(Boolean)
      .join(" · ")
  }

  return (
    <Modal
      open={open}
      title={t("leadImportPreview.title")}
      okText={t("leadImportPreview.importAnyway")}
      cancelText={t("leadImportPreview.cancel")}
      confirmLoading={loading}
      onOk={onConfirm}
      onCancel={onCancel}
    >
      <Flex vertical gap={"small"}>
        <Typography.Text type={"secondary"}>
          {t("leadImportPreview.description")}
        </Typography.Text>

        <Flex
          vertical
          gap={"small"}
          className={"max-h-96 overflow-y-auto pr-1"}
        >
          {colliding.map((row) => (
            <Flex key={row.row} vertical>
              <Typography.Text strong>
                {t("leadImportPreview.rowLabel", {
                  row: row.row,
                  name: row.lead_name
                })}
              </Typography.Text>
              {row.matches.map((match, index) => (
                <Flex
                  key={(match.id ?? "in-file") + ":" + index}
                  align={"center"}
                  gap={"small"}
                >
                  <Tag color={REASON_COLOUR[match.reason]}>
                    {t(REASON_LABEL[match.reason])}
                  </Tag>
                  <Typography.Text type={"secondary"} className={"text-xs"}>
                    {describe(match)}
                  </Typography.Text>
                </Flex>
              ))}
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Modal>
  )
}
